import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Stack, TableContainer, Typography } from '@mui/material';

import { USER_ROLES } from 'constants/userRoles';
import { HEADER_HEIGHT } from 'constants/components';
import { getUserFullName } from 'helper/getCapitalizedString';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { ColoredTable, Dialog } from 'components/complex';
import { Checkbox } from 'components/simple';

const styles = {
  dialogPaper: ({ breakpoints }) => ({
    [breakpoints.up('lg')]: {
      maxWidth: 1000,
      maxHeight: `calc(100vh - ${HEADER_HEIGHT * 2 + 8}px)`,
    },
  }),
  dialogTitle: ({ breakpoints }) => ({
    [breakpoints.up('lg')]: {
      fontWeight: 600,
      margin: 'unset',
      paddingBottom: 2,
    },
  }),
  dialogContent: ({ breakpoints }) => ({
    [breakpoints.up('lg')]: {
      overflowY: 'hidden',
      paddingBottom: 'unset',
    },
  }),
  dialogActions: ({ breakpoints }) => ({
    [breakpoints.up('lg')]: {
      paddingBlock: 4,
    },
  }),
  tableContainer: ({ palette, breakpoints }) => ({
    border: `2px solid ${palette.colors.darkPale}`,
    borderRadius: 5,
    paddingInline: 1,
    height: '100%',
    [breakpoints.up('lg')]: {
      maxHeight: 'calc(100vh - 495px)',
    },
    [breakpoints.up('xl')]: {
      maxHeight: 'calc(100vh - 505px)',
    },
    [breakpoints.up('xl')]: {
      maxHeight: 'calc(100vh - 505px)',
    },
  }),
  headerRow: ({ breakpoints, spacing }) => ({
    '& > td, & > th': { paddingBlock: 2 },
    [breakpoints.up('md')]: {
      '& > td, & > th': {
        paddingBlock: `${spacing(3.5)} !important`,
      },
    },
  }),
  tableRow: {
    '& > td, & > th': { paddingBlock: 2 },
  },
};

const CELL_KEYS = {
  NAME: 'name',
  EMAIL: 'email',
  PARTICIPANT: 'participant',
  VIEWER: 'viewer',
  NAME_EMAIL: 'name_email',
};

const confirmLabel = 'Add permissions';
const formatHeaders = (headers = []) =>
  headers.map(({ content, ...headerData }) => ({
    ...headerData,
    content: (
      <Typography variant="button" color="text.secondary">
        {content}
      </Typography>
    ),
  }));

export const LeadersPermissionsDialog = () => {
  const { onConfirm, onCancel, dialogData, isLoading } = useSelector(dialogPropsSelector);
  const { projectName, projectLeaders } = dialogData;
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const { isMdUp, isLgUp } = useBreakpointFlags();

  const isDisabledConfirm = !Object.values(selectedPermissions).some((permissions) =>
    Object.values(permissions || {}).some((permission) => permission === true),
  );

  const handlePermissionChange = (leaderId, role, checked) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [leaderId]: {
        ...prev[leaderId],
        [role]: checked,
      },
    }));
  };

  const handleConfirm = () => {
    onConfirm(selectedPermissions);
  };

  const tableHeader = [
    ...(isLgUp
      ? [
          {
            cellKey: CELL_KEYS.NAME,
            content: 'Name',
          },
          {
            cellKey: CELL_KEYS.EMAIL,
            content: 'Email',
            cellProps: {
              align: 'center',
              width: 410,
            },
          },
        ]
      : [
          {
            cellKey: CELL_KEYS.NAME_EMAIL,
            content: 'Name / Email',
          },
        ]),
    {
      cellKey: CELL_KEYS.VIEWER,
      content: 'Viewer',
      cellProps: {
        align: 'center',
        width: isMdUp ? 120 : 80,
      },
    },
    {
      cellKey: CELL_KEYS.PARTICIPANT,
      content: 'Participant',
      cellProps: {
        align: 'center',
        width: isMdUp ? 150 : 120,
      },
    },
  ];

  const tableRows = projectLeaders.map((leader) => {
    const leaderId = leader.user_id;
    const leaderName = getUserFullName(leader);

    return {
      id: leaderId,
      cells: [
        ...(isLgUp
          ? [
              {
                cellKey: CELL_KEYS.NAME,
                component: 'th',
                scope: 'row',
                content: <Typography variant="body2-bold">{leaderName}</Typography>,
              },
              {
                cellKey: CELL_KEYS.EMAIL,
                content: <Typography variant="body2">{leader.email}</Typography>,
              },
            ]
          : [
              {
                cellKey: CELL_KEYS.NAME_EMAIL,
                component: 'th',
                scope: 'row',
                content: (
                  <Stack spacing={1}>
                    <Typography variant="body2-bold">{leaderName}</Typography>
                    <Typography variant="body2">{leader.email}</Typography>
                  </Stack>
                ),
              },
            ]),
        {
          cellKey: CELL_KEYS.VIEWER,
          content: (
            <Checkbox
              checked={selectedPermissions[leaderId]?.viewer || false}
              onChange={(checked) => handlePermissionChange(leaderId, USER_ROLES.VIEWER, checked)}
            />
          ),
          cellProps: {
            align: 'center',
          },
        },
        {
          cellKey: CELL_KEYS.PARTICIPANT,
          content: (
            <Checkbox
              checked={selectedPermissions[leaderId]?.participant || false}
              onChange={(checked) => handlePermissionChange(leaderId, USER_ROLES.PARTICIPANT, checked)}
            />
          ),
          cellProps: {
            align: 'center',
          },
        },
      ],
    };
  });

  return (
    <Dialog
      isLoading={isLoading}
      id="leaders-permissions-dialog"
      title="Launch Successful!"
      onConfirm={handleConfirm}
      onCancel={onCancel}
      confirmLabel={confirmLabel}
      isDisabledConfirm={isDisabledConfirm}
      cancelLabel="Continue without Adding Permissions"
      customStyles={{
        paperStyles: styles.dialogPaper,
        titleStyles: styles.dialogTitle,
        contentStyles: styles.dialogContent,
        actionsStyles: styles.dialogActions,
      }}
    >
      <Stack spacing={{ xs: 2, lg: 1, xl: 3 }}>
        <Stack spacing={2}>
          <Typography variant="body1">
            {`You have successfully launched ${projectName} with the below Leaders.`}
          </Typography>

          <Typography variant="body1">
            {`To add survey participant and dashboard viewer permissions for these users, please tick the relevant permissions and click '${confirmLabel}'.`}
          </Typography>
        </Stack>

        <TableContainer sx={styles.tableContainer}>
          <ColoredTable
            header={formatHeaders(tableHeader)}
            rows={tableRows}
            customStyles={{
              tableRow: styles.tableRow,
              headerRow: styles.headerRow,
            }}
          />
        </TableContainer>
      </Stack>
    </Dialog>
  );
};
